import React, {
  useState,
  useCallback,
  FunctionComponent,
  useEffect,
} from 'react';
import classNames from 'classnames';
import moment from 'moment';
import _ from 'underscore';
import { forNamespace } from 'app/common/lib/util/i18n';
import { SessionStorage } from '@trello/storage';
import Banner from 'app/common/components/banner/banner';
import {
  PrimaryButton,
  ButtonSizes,
} from 'app/common/components/controls/button/button';
import styles from './AtlassianAccountOnboardingBannerV2.less';
import { Noun, Verb } from '@trello/analytics';
import { track } from './Analytics';
import { AtlassianAccountOnboardingLinks } from './AtlassianAccountOnboardingLinks';
import { N0 } from '@trello/colors';
import {
  Overlay,
  OverlayAlignment,
  OverlayBackground,
} from 'app/src/components/Overlay';
import { useAtlassianAccountOnboardingQuery } from '@trello/graphql/generated';
import {
  AtlassianAccountOnboardingOverlayV2,
  ScreenType,
} from './AtlassianAccountOnboardingOverlayV2';
import { LinkWrapper } from 'app/common/components/singlespa/link-wrapper';
import api from 'app/gamma/src/api';
import { dateDifferenceInDays } from '@trello/dates';
import { WarningType } from './v2Overlays/AccountWarning';
import { sendCrashEvent } from '@trello/error-reporting';

const RightWaveImageUrl = require('resources/images/onboarding-banner/banner-wave-right.svg');
const LeftWaveImageUrl = require('resources/images/onboarding-banner/banner-wave-left.svg');
const TacoImageUrl = require('resources/images/onboarding-banner/Taco.svg');

let analyticsContext = {};
const analyticsSource = 'atlassianAccountOnboardingBanner';
const SNOOZED_STORAGE_KEY = 'aa-banner-snoozed';
const format = forNamespace(['aa onboarding', 'banner'], {
  shouldEscapeStrings: false,
});
const query = window.location.search.split('?q=').pop();
const shouldShowSuccessOverlay =
  query && decodeURIComponent(query).includes('onboarding=success');

const isSnoozed = () => {
  const snoozed = SessionStorage.get(SNOOZED_STORAGE_KEY);
  return snoozed && moment().isBefore(snoozed.expires);
};

export const AtlassianAccountOnboardingBannerV2: FunctionComponent = () => {
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const [screen, setScreen] = useState(ScreenType.WORK_AND_OTHER);
  const [snoozed, setSnoozed] = useState(isSnoozed());
  const [snoozedCallback, setSnoozedCallback] = useState(false);
  const [warningMsg, setWarningMsg] = useState('active');

  //Date of forced migration
  const updateByDate = new Date('April 14, 2020');
  const formattedUpdateByDate = moment(updateByDate).format('MMMM Do, YYYY');

  //Show urgent message if we are within 30 days of forced migration date
  const daysToUpdateByDate = dateDifferenceInDays(new Date(), updateByDate);
  const useUrgentMessage = daysToUpdateByDate <= 30;

  const { data } = useAtlassianAccountOnboardingQuery({
    variables: { memberId: 'me' },
  });

  const me = data?.member;
  const name = me?.fullName;
  const id = me?.id;
  const isAaMastered = me?.isAaMastered;
  const aaEmail = me?.aaEmail;
  const aaId = me?.aaId;
  const aaLogin = me?.logins.find(({ email }) => email === aaEmail);
  const aaLoginId = aaLogin?.id;
  const ent = me && !_.isEmpty(me.enterprises) ? me.enterprises[0] : undefined;
  const orgName = ent?.displayName;

  const checkAccountStatus = async () => {
    try {
      const response = await api.client.getAccountStatus();
      const status = response._value;

      if (status === 'closed' || status === 'inactive') {
        setWarningMsg(
          status === 'closed' ? WarningType.deletion : WarningType.deactivate,
        );
      } else {
        setWarningMsg(status);
      }
    } catch (error) {
      sendCrashEvent({
        error,
      });
    }
  };

  const snooze = useCallback(() => {
    SessionStorage.set(SNOOZED_STORAGE_KEY, {
      expires: moment()
        .add(12, 'hours')
        .startOf('hour'),
    });

    setSnoozed(true);
    track({
      verb: Verb.HIDES,
      noun: Noun.BANNER,
      method: 'by clicking dismiss button',
      context: analyticsContext,
      actionSubject: 'button',
      actionSubjectId: 'atlassianAccountOnboardingBannerSnoozeButton',
      action: 'clicked',
      source: analyticsSource,
    });
  }, []);

  const onClickRemove = () => {
    checkAccountStatus();
    track({
      verb: Verb.REMOVES,
      noun: Noun.EMAIL,
      method: 'via onboarding modal',
      context: analyticsContext,
      source: analyticsSource,
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'atlassianOnboardingRemoveEmailButton',
    });
  };

  const onClickContinue = useCallback(() => {
    checkAccountStatus();
    setScreen(ScreenType.ACCOUNT_TYPE);
    setOverlayOpen(true);
    track({
      verb: Verb.INITIATES,
      noun: Noun.ONBOARDING_MENU,
      method: 'by clicking continue button',
      context: analyticsContext,
      source: analyticsSource,
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'atlassianAccountOnboardingContinueButton',
    });
  }, []);

  const closeOverlayImplicitly = useCallback(() => {
    setOverlayOpen(false);
    track({
      verb: Verb.DISMISSES,
      noun: Noun.ONBOARDING_MENU,
      method: 'with an implicit input',
      context: analyticsContext,
      source: analyticsSource,
      action: 'closed',
      actionSubject: 'overlay',
      actionSubjectId: 'atlassianAccountOnboardingOverlay',
    });
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false);
    track({
      verb: Verb.DISMISSES,
      noun: Noun.ONBOARDING_MENU,
      method: 'with dismiss button',
      context: analyticsContext,
      source: analyticsSource,
      action: 'closed',
      actionSubject: 'overlay',
      actionSubjectId: 'atlassianAccountOnboardingOverlay',
    });
  }, []);

  const onShowUrgentUpdate = useCallback(() => {
    setScreen(ScreenType.URGENT_UPDATE);
    setOverlayOpen(true);
    track({
      verb: Verb.DISPLAYS,
      noun: Noun.ONBOARDING_MENU,
      method: 'through urgent update overlay requirements',
      context: analyticsContext,
      actionSubject: 'overlay',
      actionSubjectId: 'atlassianAccountOnboardingUrgentUpdate',
      action: 'viewed',
      source: analyticsSource,
    });
  }, []);

  const onShowAccountWarning = useCallback(() => {
    setScreen(ScreenType.ACCOUNT_WARNING);
    setOverlayOpen(true);
    track({
      verb: Verb.DISPLAYS,
      noun: Noun.ATLASSIAN_ACCOUNT_ONBOARDING,
      method: 'by receiving deleted or deactivated account status',
      context: analyticsContext,
      actionSubject: 'overlay',
      actionSubjectId: 'atlassianAccountOnboardingWarningOverlay',
      action: 'viewed',
      source: analyticsSource,
    });
  }, []);

  const onShowAccountConfirmation = useCallback(() => {
    setScreen(ScreenType.ACCOUNT_CONFIRMATION);
    setOverlayOpen(true);
    track({
      verb: Verb.DISPLAYS,
      noun: Noun.ATLASSIAN_ACCOUNT_ONBOARDING,
      method: 'by completing account onboarding flow',
      context: analyticsContext,
      actionSubject: 'overlay',
      actionSubjectId: 'atlassianAccountOnboardingSuccessOverlay',
      action: 'viewed',
      source: analyticsSource,
    });
  }, []);

  useEffect(() => {
    new Image().src = LeftWaveImageUrl;
    new Image().src = RightWaveImageUrl;
    new Image().src = TacoImageUrl;

    if (shouldShowSuccessOverlay) {
      onShowAccountConfirmation();
    } else {
      if (!isAaMastered && aaId) {
        checkAccountStatus();
        if (warningMsg !== 'active') {
          onShowAccountWarning();
        } else if (useUrgentMessage) {
          onShowUrgentUpdate();
        }
      }
    }
  }, [
    isAaMastered,
    aaId,
    onShowAccountConfirmation,
    onShowAccountWarning,
    onShowUrgentUpdate,
    useUrgentMessage,
    warningMsg,
  ]);

  // Ensure we have all the data necessary to render (none of our fields are null)
  if (
    !id ||
    !me ||
    !name ||
    !ent ||
    !orgName ||
    !aaEmail ||
    !aaId ||
    !aaLogin ||
    !aaLoginId
  ) {
    return null;
  }

  analyticsContext = {
    enterpriseId: ent.id,
    emailCount: me.logins.length,
    v2: true,
    urgent: useUrgentMessage,
  };

  // The success overlay should be displayed when the account is aaMastered
  // regardless if snooze for the banner was set
  if (shouldShowSuccessOverlay && me.isAaMastered) {
    return isOverlayOpen ? (
      <Overlay
        background={OverlayBackground.LIGHT}
        onClose={closeOverlayImplicitly}
        alignment={OverlayAlignment.TOP}
      >
        <AtlassianAccountOnboardingOverlayV2
          id={id}
          name={name}
          updateByDate={formattedUpdateByDate}
          warningDate={formattedUpdateByDate}
          warningMsg={warningMsg}
          urgent={useUrgentMessage}
          aaEmail={aaEmail}
          logins={me && me.logins}
          orgName={orgName}
          dismiss={closeOverlay}
          screen={screen}
          setScreen={setScreen}
          analyticsContext={analyticsContext}
        />
      </Overlay>
    ) : null;
  }

  // We only want to show the banner to members who are not already aa mastered,
  // and have an enrolled date
  const shouldDisplay = !me.isAaMastered && me.aaEnrolledDate;
  if (!shouldDisplay) {
    return null;
  }

  // If we are snoozed check the time the snooze expires and
  // reset the state once we hit expiration
  if (snoozed) {
    if (!snoozedCallback) {
      const storedSnooze = SessionStorage.get(SNOOZED_STORAGE_KEY);
      const timeDifference = moment(storedSnooze.expires).diff(moment());

      setTimeout(() => {
        SessionStorage.unset(SNOOZED_STORAGE_KEY);
        setSnoozed(false);
        setSnoozedCallback(false);
      }, timeDifference);

      setSnoozedCallback(true);
    }

    return null;
  }

  return (
    <div>
      <Banner
        onDismiss={
          useUrgentMessage || warningMsg !== 'active' ? undefined : snooze
        }
        infoUrl={AtlassianAccountOnboardingLinks.Help}
        bannerColor={N0}
        className={styles.banner}
        shadow
        roundedCorners
        alignCloseTop
      >
        <div className={styles.container}>
          <div className={styles.body}>
            {warningMsg !== 'active' ? (
              <WarningBannerContent
                aaEmail={aaEmail}
                orgName={orgName}
                warningDate={formattedUpdateByDate}
                warningMsg={warningMsg}
                urgent={useUrgentMessage}
                onClickRemove={onClickRemove}
              />
            ) : (
              <>
                <div>
                  <span>
                    {format('update by', { date: formattedUpdateByDate })}
                  </span>
                  <p>
                    {format('claim msg', {
                      orgName,
                    })}{' '}
                    <u>{aaEmail}</u>
                  </p>
                </div>
                <div className={styles.controls}>
                  <PrimaryButton
                    className={styles.onlyWorkButton}
                    onClick={onClickContinue}
                    size={ButtonSizes.WIDE}
                  >
                    {format('continue')}
                  </PrimaryButton>
                </div>
              </>
            )}
            {isOverlayOpen && (
              <Overlay
                background={OverlayBackground.LIGHT}
                onClose={closeOverlayImplicitly}
                alignment={OverlayAlignment.TOP}
              >
                <AtlassianAccountOnboardingOverlayV2
                  id={id}
                  name={name}
                  updateByDate={formattedUpdateByDate}
                  warningDate={formattedUpdateByDate}
                  warningMsg={warningMsg}
                  urgent={useUrgentMessage}
                  aaEmail={aaEmail}
                  logins={me && me.logins}
                  orgName={orgName}
                  dismiss={closeOverlay}
                  screen={screen}
                  setScreen={setScreen}
                  analyticsContext={analyticsContext}
                />
              </Overlay>
            )}
          </div>
        </div>
        <img alt="" src={TacoImageUrl} className={styles.tacoImg} />
        <img
          alt=""
          src={LeftWaveImageUrl}
          className={classNames(styles.waveImg, styles.leftWaveImg)}
        />
        <img
          alt=""
          src={RightWaveImageUrl}
          className={classNames(styles.waveImg, styles.rightWaveImg)}
        />
      </Banner>
    </div>
  );
};

interface WarningBannerContentProps {
  aaEmail: string;
  orgName: string;
  warningDate: string;
  warningMsg: string;
  urgent: boolean;
  onClickRemove: () => void;
}

export const WarningBannerContent: FunctionComponent<WarningBannerContentProps> = ({
  aaEmail,
  orgName,
  warningDate,
  warningMsg,
  onClickRemove,
}) => (
  <div className={styles.warningMsg}>
    <div>
      <p>
        <b>
          {warningMsg === WarningType.deactivate
            ? format('deactivated title', {
                date: warningDate,
              })
            : format('deleted title', {
                date: warningDate,
              })}
        </b>
      </p>
      <p>
        {format(warningMsg, {
          org: orgName,
        })}{' '}
        {format('keep using account', {
          date: warningDate,
          aaEmail: aaEmail,
        })}
      </p>
    </div>
    <div className={styles.controls}>
      <LinkWrapper
        className={styles.warningBannerButton}
        href="/me"
        role="link"
      >
        <PrimaryButton size={ButtonSizes.WIDE} onClick={onClickRemove}>
          {format('remove email')}
        </PrimaryButton>
      </LinkWrapper>
    </div>
  </div>
);
